import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import EditNoteRounded from '@mui/icons-material/EditNoteRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import TeacherStudentCard, { TeacherStudentAction, TeacherStudentGrid } from './TeacherStudentCard';
import TeacherAwardFeedback from './TeacherAwardFeedback';
import './TeacherAttendanceBoard.css';

export function attendanceToday() {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${values.year}-${values.month}-${values.day}`;
}

const statuses = [{ value: 'absent', label: 'Yok' }, { value: 'late', label: 'Geç' }, { value: 'present', label: 'Tam' }];
const statusLabel = (status) => statuses.find((item) => item.value === status)?.label || 'Kayıtsız';
const collator = new Intl.Collator('tr', { sensitivity: 'base', numeric: true });
const sortStudents = (students) => [...students].sort((a, b) => collator.compare(`${a.first_name} ${a.last_name}`, `${b.first_name} ${b.last_name}`) || Number(a.student_id) - Number(b.student_id));

const TeacherAttendanceBoard = forwardRef(function TeacherAttendanceBoard({ onRequest, classCode, onBack, onLogout, onPendingChange }, ref) {
  const [students, setStudents] = useState([]);
  const [day, setDay] = useState(attendanceToday);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pending, setPending] = useState({});
  const [bulkPending, setBulkPending] = useState(false);
  const [errors, setErrors] = useState({});
  const [drafts, setDrafts] = useState({});
  const [openNotes, setOpenNotes] = useState({});
  const [feedback, setFeedback] = useState({});
  const [notice, setNotice] = useState(null);
  const [dayChanged, setDayChanged] = useState(false);
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [leaveDialog, setLeaveDialog] = useState(false);
  const dayRef = useRef(day);
  const operations = useRef(new Set());
  const bulkLock = useRef(false);
  const leaveAction = useRef(null);
  const sequence = useRef(0);
  const alive = useRef(true);
  const loadGeneration = useRef(0);
  const hasPending = bulkPending || Object.keys(pending).length > 0;
  const hasUnverified = Object.values(errors).some((error) => error?.unverified);
  const hasDirtyNotes = Object.entries(drafts).some(([id, text]) => text !== (students.find((student) => String(student.student_id) === id)?.notes || ''));

  const request = useCallback(async (path, options = {}, timeout = 12000) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try { return await onRequest(path, { ...options, signal: controller.signal }); }
    finally { clearTimeout(timer); }
  }, [onRequest]);

  const loadBoard = useCallback(async (targetDay) => {
    const generation = ++loadGeneration.current;
    setLoading(true);
    setLoadError('');
    try {
      const board = await request(`attendance?date=${targetDay}`);
      if (!alive.current || generation !== loadGeneration.current) return;
      setStudents(sortStudents(board.students || []));
      dayRef.current = board.date;
      setDay(board.date);
      setDayChanged(board.date !== attendanceToday());
      setNeedsRefresh(false);
      setErrors({});
      setNotice(null);
    } catch (error) {
      if (alive.current && generation === loadGeneration.current && error.status !== 401) setLoadError('Yoklama yüklenemedi. Yeniden deneyin.');
    } finally { if (alive.current && generation === loadGeneration.current) setLoading(false); }
  }, [request]);

  useEffect(() => {
    alive.current = true;
    loadBoard(attendanceToday());
    return () => { alive.current = false; loadGeneration.current += 1; };
  }, [loadBoard]);

  useEffect(() => {
    const checkDay = () => { if (attendanceToday() !== dayRef.current) setDayChanged(true); };
    const timer = setInterval(checkDay, 30000);
    window.addEventListener('focus', checkDay);
    document.addEventListener('visibilitychange', checkDay);
    return () => { clearInterval(timer); window.removeEventListener('focus', checkDay); document.removeEventListener('visibilitychange', checkDay); };
  }, []);

  useEffect(() => { onPendingChange?.(hasPending); }, [hasPending, onPendingChange]);
  useEffect(() => () => onPendingChange?.(false), [onPendingChange]);
  useEffect(() => {
    if (!Object.keys(feedback).length) return undefined;
    const timer = setTimeout(() => setFeedback({}), 2200);
    return () => clearTimeout(timer);
  }, [feedback]);
  useEffect(() => {
    if (!hasDirtyNotes) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasDirtyNotes]);

  const attemptLeave = useCallback((action) => {
    if (operations.current.size || bulkLock.current) return;
    if (hasDirtyNotes) { leaveAction.current = action; setLeaveDialog(true); }
    else action();
  }, [hasDirtyNotes]);
  useImperativeHandle(ref, () => ({ requestLeave: attemptLeave }), [attemptLeave]);

  const updateRecord = (record) => {
    if (!alive.current || record.date !== dayRef.current) return;
    setStudents((current) => current.map((student) => Number(student.student_id) === Number(record.student_id)
      ? { ...student, status: record.status, notes: record.notes || '' } : student));
  };
  const setCardError = (id, error) => setErrors((current) => ({ ...current, [id]: error }));
  const verifyCard = async (id) => {
    const board = await request(`attendance?date=${dayRef.current}`, {}, 8000);
    const record = board.students.find((student) => Number(student.student_id) === Number(id));
    if (!record) throw new Error('Öğrenci kaydı bulunamadı.');
    updateRecord({ ...record, date: board.date });
    return record;
  };
  const currentDay = () => {
    if (attendanceToday() === dayRef.current) return true;
    setDayChanged(true);
    return false;
  };

  const save = async (student, kind, value) => {
    const id = String(student.student_id);
    if (!currentDay() || needsRefresh || operations.current.has(id) || bulkLock.current || errors[id]?.unverified) return;
    if (kind === 'status' && student.status === value) return;
    if (kind === 'note' && !student.status) return;
    operations.current.add(id);
    setPending((current) => ({ ...current, [id]: { kind, value } }));
    setCardError(id, null);
    setFeedback((current) => { const next = { ...current }; delete next[id]; return next; });
    try {
      const result = await request(`attendance/students/${id}${kind === 'note' ? '/note' : ''}`, {
        method: 'PATCH', body: JSON.stringify({ date: dayRef.current, ...(kind === 'note' ? { notes: value } : { status: value }) }),
      });
      if (!alive.current) return;
      updateRecord(result.record);
      if (kind === 'note') setDrafts((current) => { const next = { ...current }; if (next[id] === value) delete next[id]; return next; });
      const label = kind === 'status' && value === 'present' ? (result.awarded_points === 2 ? '⭐ +2' : '✨ Tamam!') : null;
      setFeedback((current) => ({ ...current, [id]: { key: ++sequence.current, label } }));
    } catch (error) {
      if (!alive.current || error.status === 401) return;
      try {
        const confirmed = await verifyCard(id);
        if (!alive.current) return;
        const applied = kind === 'status' ? confirmed.status === value : (confirmed.notes || '') === value;
        setCardError(id, { message: applied ? 'Bağlantı kesildi; sunucudaki kayıt doğrulandı.' : 'Yoklama kaydedilemedi. Tekrar deneyin.', kind, value, applied });
      } catch (verificationError) {
        if (alive.current && verificationError.status !== 401) setCardError(id, { message: 'Kayıt sonucu doğrulanamadı. Yeniden yazmadan önce durumu yenileyin.', unverified: true });
      }
    } finally {
      operations.current.delete(id);
      if (alive.current) setPending((current) => { const next = { ...current }; delete next[id]; return next; });
    }
  };

  const reconcile = async (id) => {
    if (operations.current.has(id) || bulkLock.current || !currentDay()) return;
    operations.current.add(id);
    setPending((current) => ({ ...current, [id]: { kind: 'verify' } }));
    try { await verifyCard(id); if (alive.current) setCardError(id, null); }
    catch (error) { if (alive.current && error.status !== 401) setCardError(id, { message: 'Durum yüklenemedi. Tekrar yenileyin.', unverified: true }); }
    finally { operations.current.delete(id); if (alive.current) setPending((current) => { const next = { ...current }; delete next[id]; return next; }); }
  };

  const bulkPresent = async () => {
    if (!currentDay() || needsRefresh || hasUnverified || operations.current.size || bulkLock.current) return;
    bulkLock.current = true;
    setBulkPending(true);
    setNotice(null);
    try {
      const result = await request('attendance/mark-unrecorded-present', { method: 'POST', body: JSON.stringify({ date: dayRef.current }) });
      if (!alive.current) return;
      for (const record of result.records || []) updateRecord(record);
      setNotice({ severity: 'success', text: `✅ ${result.updated} öğrenci mevcut olarak işaretlendi.` });
    } catch (error) {
      if (!alive.current || error.status === 401) return;
      try {
        const board = await request(`attendance?date=${dayRef.current}`, {}, 8000);
        if (alive.current) {
          setStudents(sortStudents(board.students || []));
          setNotice({ severity: 'warning', text: 'Bağlantı kesildi; sunucudaki yoklama yeniden yüklendi. Sonucu kontrol edin.' });
        }
      } catch (verificationError) {
        if (alive.current && verificationError.status !== 401) {
          setNeedsRefresh(true);
          setNotice({ severity: 'error', text: 'Toplu işlemin sonucu doğrulanamadı. Yoklamayı yenileyin.' });
        }
      }
    } finally { bulkLock.current = false; if (alive.current) setBulkPending(false); }
  };

  const counts = useMemo(() => students.reduce((result, student) => {
    result[student.status || 'unrecorded'] += 1; return result;
  }, { present: 0, late: 0, absent: 0, unrecorded: 0 }), [students]);
  const refresh = () => {
    if (hasPending) return;
    const nextDay = attendanceToday();
    const action = () => { if (nextDay !== dayRef.current) { setDrafts({}); setOpenNotes({}); setFeedback({}); } loadBoard(nextDay); };
    if (nextDay !== dayRef.current) attemptLeave(action); else action();
  };
  const dateLabel = new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Istanbul' }).format(new Date(`${day}T12:00:00+03:00`));

  return (
    <Box className="ta-board">
      <header className="ta-heading">
        <Box className="ta-heading__copy">
          <Button startIcon={<ArrowBackRounded />} onClick={() => attemptLeave(onBack)} disabled={hasPending}>Öğretmen Modu</Button>
          <Typography component="h1">Bugünün Yoklaması</Typography>
          <Typography>{classCode || 'Bu'} Sınıfı · Öğretmen Modu</Typography>
        </Box>
        <Box className="ta-heading__actions">
          <span className="ta-date">{dayChanged ? 'Görüntülenen gün' : 'Bugün'} — {dateLabel}</span>
          <Button variant="outlined" onClick={() => attemptLeave(onLogout)} disabled={hasPending}>Mini Görevlere Dön</Button>
        </Box>
      </header>
      <Box className="ta-toolbar">
        <Box className="ta-counts" aria-label="Yoklama özeti">
          {[['present', 'Tam'], ['late', 'Geç'], ['absent', 'Yok'], ['unrecorded', 'Kayıtsız']].map(([status, label]) => (
            <Box key={status} className={`ta-count ta-count--${status}`}><strong>{counts[status]}</strong><span>{label}</span></Box>
          ))}
        </Box>
        <Button variant="contained" disabled={loading || hasPending || dayChanged || needsRefresh || hasUnverified || counts.unrecorded === 0}
          onClick={bulkPresent} startIcon={bulkPending ? <CircularProgress size={18} color="inherit" /> : null}>Kayıtsızları Mevcut Yap</Button>
      </Box>
      {dayChanged && <Alert severity="warning" className="ta-notice" action={<Button disabled={hasPending} onClick={refresh}>Yenile</Button>}>Gün değişti; yoklamayı yenileyin.</Alert>}
      {loadError && <Alert severity="error" className="ta-notice" action={<Button onClick={refresh}>Yeniden dene</Button>}>{loadError}</Alert>}
      {notice && <Alert severity={notice.severity} className="ta-notice" onClose={needsRefresh ? undefined : () => setNotice(null)}
        action={needsRefresh ? <Button disabled={hasPending} onClick={refresh}>Yenile</Button> : undefined}>{notice.text}</Alert>}
      {loading && students.length === 0 && <Box className="ta-loading"><CircularProgress /><Typography>Bugünün yoklaması yükleniyor…</Typography></Box>}
      {!loading && !loadError && students.length === 0 && <Box className="ta-empty">Bu sınıfta henüz öğrenci bulunmuyor.</Box>}
      <TeacherStudentGrid label="Öğrencilerin yoklaması" busy={loading}>
        {students.map((student) => {
          const id = String(student.student_id);
          const name = `${student.first_name} ${student.last_name}`.trim();
          const operation = pending[id];
          const disabled = !!operation || bulkPending || loading || dayChanged || needsRefresh || !!errors[id]?.unverified;
          const selectedStatus = operation?.kind === 'status' ? operation.value : student.status;
          const draft = drafts[id] ?? student.notes ?? '';
          const dirty = draft !== (student.notes || '');
          return <TeacherStudentCard key={id} studentId={id} name={name} busy={!!operation || bulkPending}
            avatarId={student.avatar_id} starterEmoji={student.starter_emoji}
            ariaLabel={`${name} yoklaması`} statusLabel={statusLabel(student.status)}
            tone={({ present: 'success', late: 'warning', absent: 'danger' })[student.status] || 'neutral'}
            avatarOverlay={feedback[id]?.label && <TeacherAwardFeedback key={feedback[id].key} label={feedback[id].label} studentName={name} />}
            identityAction={<IconButton aria-label={`${name} açıklaması`} aria-expanded={!!openNotes[id]}
              onClick={() => setOpenNotes((current) => ({ ...current, [id]: !current[id] }))} className="ta-note-toggle">
              <Badge color="primary" variant="dot" invisible={!student.notes}><EditNoteRounded /></Badge>
            </IconButton>}
            actionColumns={3} actionsLabel={`${name} yoklama durumu`}
            actions={statuses.map((status) => <TeacherStudentAction loading={operation?.kind === 'status' && operation.value === status.value} key={status.value} className={`ta-choice ta-choice--${status.value}`}
              aria-pressed={selectedStatus === status.value} disabled={disabled}
              onClick={() => save(student, 'status', status.value)}>{status.label}</TeacherStudentAction>)}>
            {operation?.kind === 'verify' && <Box className="ta-save-state" role="status">
              <CircularProgress size={14} /> Kontrol ediliyor…
            </Box>}
            {errors[id] && <Alert severity={errors[id].applied ? 'info' : 'error'} className="ta-card__error">
              {errors[id].message}
              {errors[id].unverified ? <Button size="small" onClick={() => reconcile(id)} disabled={hasPending}>Durumu yenile</Button>
                : !errors[id].applied && <Button size="small" onClick={() => save(student, errors[id].kind, errors[id].kind === 'note' ? draft : errors[id].value)} disabled={disabled}>Tekrar dene</Button>}
            </Alert>}
            {openNotes[id] && <Box className="ta-note-editor">
              <TextField label={`${name} açıklaması`} multiline minRows={2} fullWidth value={draft} disabled={operation?.kind === 'note'}
                inputProps={{ maxLength: 500 }} onChange={(event) => setDrafts((current) => ({ ...current, [id]: event.target.value }))} />
              {!student.status && <Typography className="ta-note-hint">Notu kaydetmek için önce yoklama durumunu seçin.</Typography>}
              <Box className="ta-note-actions"><TeacherStudentAction fullWidth={false} loading={operation?.kind === 'note'} variant="contained" disabled={!dirty || !student.status || disabled} onClick={() => save(student, 'note', draft)}>Notu Kaydet</TeacherStudentAction>
                <Button disabled={!!operation} onClick={() => { setDrafts((current) => { const next = { ...current }; delete next[id]; return next; }); setOpenNotes((current) => ({ ...current, [id]: false })); }}>Vazgeç</Button></Box>
            </Box>}
          </TeacherStudentCard>;
        })}
      </TeacherStudentGrid>
      {!loading && students.length > 0 && <Button className="ta-refresh" startIcon={<RefreshRounded />} disabled={hasPending} onClick={refresh}>Yoklamayı Yenile</Button>}
      <Dialog open={leaveDialog} onClose={() => setLeaveDialog(false)} aria-labelledby="attendance-leave-title">
        <DialogTitle id="attendance-leave-title">Kaydedilmemiş açıklamanız var.</DialogTitle>
        <DialogContent>Kaydetmeden çıkarsanız değişiklikler kaybolacak.</DialogContent>
        <DialogActions><Button onClick={() => setLeaveDialog(false)}>Düzenlemeye Dön</Button><Button color="error" onClick={() => { setLeaveDialog(false); setDrafts({}); const action = leaveAction.current; leaveAction.current = null; action?.(); }}>Kaydetmeden Çık</Button></DialogActions>
      </Dialog>
    </Box>
  );
});

export default TeacherAttendanceBoard;
